// ─── Filter framework: the icon catalog (f-02062) ─────────────────
// 96 Lucide icon names (kebab case), 8 categories × 12, that a saved filter
// may carry. The web renders them through a STATIC typed import map
// (apps/web/src/lib/filterIconMap.ts: Record<FilterIconName, LucideIcon>), so
// tsc fails on a missing, extra or misspelt name. Bar chrome (search, sort,
// plus, grip, ✕) does NOT use these: it goes through the house <Icon>.
//
// Every name was checked against lucide-react 1.17.0's dist/esm/icons/*.mjs.
// Four meaning clashes with existing PCP glyphs were swapped out on review:
// sparkles → lightbulb, search → telescope, loader → circle-ellipsis,
// refresh-cw → rotate-ccw.
import { isEmptyToken, isMeToken, listFilterConditions } from './model.js';
export const FILTER_ICON_CATEGORIES = [
    {
        key: 'status', en: 'Status & workflow', da: 'Status og forløb',
        icons: ['circle-dot', 'circle-check', 'circle-x', 'circle-pause', 'circle-dashed', 'circle-ellipsis', 'hourglass', 'rotate-ccw', 'archive', 'inbox', 'check-check', 'list-checks'],
    },
    {
        key: 'people', en: 'People & ownership', da: 'Personer og ejerskab',
        icons: ['user', 'users', 'user-check', 'user-x', 'user-plus', 'user-round-search', 'contact', 'id-card', 'handshake', 'crown', 'shield-user', 'headset'],
    },
    {
        key: 'time', en: 'Time & dates', da: 'Tid og datoer',
        icons: ['clock', 'alarm-clock', 'calendar', 'calendar-days', 'calendar-clock', 'calendar-check', 'calendar-x', 'calendar-range', 'history', 'timer', 'sunrise', 'moon'],
    },
    {
        key: 'communication', en: 'Communication', da: 'Kommunikation',
        icons: ['mail', 'mail-open', 'message-square', 'messages-square', 'message-circle', 'reply', 'forward', 'phone', 'send', 'at-sign', 'megaphone', 'bell'],
    },
    {
        key: 'priority', en: 'Priority & alerts', da: 'Prioritet og advarsler',
        icons: ['flag', 'triangle-alert', 'octagon-alert', 'circle-alert', 'siren', 'flame', 'zap', 'shield-alert', 'bell-ring', 'star', 'bookmark', 'thumbs-down'],
    },
    {
        key: 'work', en: 'Work & objects', da: 'Arbejde og objekter',
        icons: ['ticket', 'bug', 'wrench', 'hammer', 'package', 'file-text', 'paperclip', 'clipboard-list', 'notebook-pen', 'folder', 'layers', 'tag'],
    },
    {
        key: 'places', en: 'Places & systems', da: 'Steder og systemer',
        icons: ['building', 'building-2', 'store', 'landmark', 'globe', 'house', 'server', 'database', 'cloud', 'monitor', 'smartphone', 'map-pin'],
    },
    {
        key: 'symbols', en: 'Symbols & filters', da: 'Symboler og filtre',
        icons: ['funnel', 'telescope', 'lightbulb', 'heart', 'circle', 'square', 'triangle', 'hexagon', 'diamond', 'hash', 'eye', 'target'],
    },
];
/** All 96 names, in catalog order. */
export const FILTER_ICON_NAMES = FILTER_ICON_CATEGORIES.flatMap((c) => [...c.icons]);
/** The generic fallback when a filter has no conditions to derive from. */
export const DEFAULT_FILTER_ICON = 'funnel';
const ICON_SET = new Set(FILTER_ICON_NAMES);
export function isFilterIconName(v) {
    return typeof v === 'string' && ICON_SET.has(v);
}
export function filterIconCategory(name) {
    for (const c of FILTER_ICON_CATEGORIES)
        if (c.icons.includes(name))
            return c.key;
    return 'symbols';
}
/** English + Danish labels and search synonyms for the picker (lucide-react
 *  ships no tag metadata at runtime). */
export const FILTER_ICON_INFO = {
    'circle-dot': { en: 'Open', da: 'Åben', tags: ['status', 'active', 'aktiv'] },
    'circle-check': { en: 'Done', da: 'Færdig', tags: ['closed', 'resolved', 'lukket', 'løst'] },
    'circle-x': { en: 'Cancelled', da: 'Annulleret', tags: ['rejected', 'afvist', 'no'] },
    'circle-pause': { en: 'On hold', da: 'På hold', tags: ['paused', 'waiting', 'venter', 'pause'] },
    'circle-dashed': { en: 'Draft', da: 'Kladde', tags: ['new', 'ny', 'pending', 'unstarted'] },
    'circle-ellipsis': { en: 'In progress', da: 'I gang', tags: ['working', 'pending', 'igang'] },
    hourglass: { en: 'Waiting', da: 'Venter', tags: ['pending', 'time', 'sla'] },
    'rotate-ccw': { en: 'Reopened', da: 'Genåbnet', tags: ['again', 'igen', 'undo', 'repeat'] },
    archive: { en: 'Archive', da: 'Arkiv', tags: ['old', 'gammel', 'stored'] },
    inbox: { en: 'Inbox', da: 'Indbakke', tags: ['all', 'alle', 'incoming'] },
    'check-check': { en: 'Reviewed', da: 'Gennemset', tags: ['read', 'seen', 'læst', 'set'] },
    'list-checks': { en: 'Checklist', da: 'Tjekliste', tags: ['tasks', 'opgaver', 'todo'] },
    user: { en: 'Person', da: 'Person', tags: ['assignee', 'ansvarlig', 'agent'] },
    users: { en: 'Team', da: 'Team', tags: ['group', 'gruppe', 'people', 'personer'] },
    'user-check': { en: 'Mine', da: 'Mine', tags: ['me', 'mig', 'assigned', 'tildelt'] },
    'user-x': { en: 'Unassigned', da: 'Ikke tildelt', tags: ['nobody', 'ingen', 'none'] },
    'user-plus': { en: 'New person', da: 'Ny person', tags: ['add', 'tilføj', 'new customer'] },
    'user-round-search': { en: 'Find person', da: 'Find person', tags: ['lookup', 'søg', 'search'] },
    contact: { en: 'Contact', da: 'Kontakt', tags: ['customer', 'kunde', 'person'] },
    'id-card': { en: 'ID card', da: 'ID-kort', tags: ['identity', 'identitet', 'account'] },
    handshake: { en: 'Partner', da: 'Partner', tags: ['deal', 'aftale', 'agreement'] },
    crown: { en: 'VIP', da: 'VIP', tags: ['important', 'vigtig', 'premium'] },
    'shield-user': { en: 'Admin', da: 'Administrator', tags: ['security', 'sikkerhed', 'role'] },
    headset: { en: 'Support', da: 'Support', tags: ['agent', 'helpdesk', 'kundeservice'] },
    clock: { en: 'Time', da: 'Tid', tags: ['hour', 'time', 'tidspunkt'] },
    'alarm-clock': { en: 'Due', da: 'Forfalden', tags: ['deadline', 'frist', 'alarm'] },
    calendar: { en: 'Date', da: 'Dato', tags: ['day', 'dag', 'calendar', 'kalender'] },
    'calendar-days': { en: 'Created', da: 'Oprettet', tags: ['days', 'dage', 'new'] },
    'calendar-clock': { en: 'Updated', da: 'Opdateret', tags: ['modified', 'ændret', 'recent'] },
    'calendar-check': { en: 'Closed date', da: 'Lukkedato', tags: ['finished', 'afsluttet'] },
    'calendar-x': { en: 'Overdue', da: 'Overskredet', tags: ['late', 'sent', 'missed'] },
    'calendar-range': { en: 'Period', da: 'Periode', tags: ['range', 'interval', 'between'] },
    history: { en: 'History', da: 'Historik', tags: ['past', 'tidligere', 'old'] },
    timer: { en: 'Timer', da: 'Timer', tags: ['sla', 'duration', 'varighed'] },
    sunrise: { en: 'Today', da: 'I dag', tags: ['morning', 'morgen', 'new day'] },
    moon: { en: 'Night', da: 'Nat', tags: ['after hours', 'efter lukketid', 'evening'] },
    mail: { en: 'Email', da: 'E-mail', tags: ['mail', 'post', 'inbox'] },
    'mail-open': { en: 'Read mail', da: 'Læst mail', tags: ['opened', 'åbnet'] },
    'message-square': { en: 'Message', da: 'Besked', tags: ['comment', 'kommentar', 'chat'] },
    'messages-square': { en: 'Conversation', da: 'Samtale', tags: ['threads', 'tråde', 'dialog'] },
    'message-circle': { en: 'Chat', da: 'Chat', tags: ['talk', 'snak', 'comment'] },
    reply: { en: 'Reply', da: 'Svar', tags: ['answer', 'besvar', 'respond'] },
    forward: { en: 'Forward', da: 'Videresend', tags: ['send on', 'escalate'] },
    phone: { en: 'Phone', da: 'Telefon', tags: ['call', 'opkald', 'ring'] },
    send: { en: 'Sent', da: 'Sendt', tags: ['outgoing', 'udgående'] },
    'at-sign': { en: 'Channel', da: 'Kanal', tags: ['email', 'mention', 'nævnt'] },
    megaphone: { en: 'Announcement', da: 'Meddelelse', tags: ['broadcast', 'news', 'nyhed'] },
    bell: { en: 'Notification', da: 'Notifikation', tags: ['alert', 'besked', 'reminder'] },
    flag: { en: 'Priority', da: 'Prioritet', tags: ['flag', 'important', 'vigtig'] },
    'triangle-alert': { en: 'Warning', da: 'Advarsel', tags: ['caution', 'pas på', 'risk'] },
    'octagon-alert': { en: 'Critical', da: 'Kritisk', tags: ['stop', 'blocker', 'blokerende'] },
    'circle-alert': { en: 'Attention', da: 'Opmærksomhed', tags: ['info', 'notice', 'bemærk'] },
    siren: { en: 'Escalated', da: 'Eskaleret', tags: ['urgent', 'akut', 'emergency'] },
    flame: { en: 'Hot', da: 'Varm', tags: ['fire', 'trending', 'brand'] },
    zap: { en: 'Fast', da: 'Hurtig', tags: ['quick', 'lightning', 'lyn'] },
    'shield-alert': { en: 'Security', da: 'Sikkerhed', tags: ['risk', 'breach', 'brud'] },
    'bell-ring': { en: 'Reminder', da: 'Påmindelse', tags: ['alarm', 'ringing'] },
    star: { en: 'Favourite', da: 'Favorit', tags: ['star', 'stjerne', 'important'] },
    bookmark: { en: 'Bookmark', da: 'Bogmærke', tags: ['saved', 'gemt', 'later'] },
    'thumbs-down': { en: 'Negative', da: 'Negativ', tags: ['bad', 'dårlig', 'unhappy', 'utilfreds'] },
    ticket: { en: 'Ticket', da: 'Sag', tags: ['case', 'billet', 'request'] },
    bug: { en: 'Bug', da: 'Fejl', tags: ['error', 'defect', 'defekt'] },
    wrench: { en: 'Fix', da: 'Ret', tags: ['repair', 'reparer', 'tool', 'værktøj'] },
    hammer: { en: 'Build', da: 'Byg', tags: ['work', 'arbejde', 'tool'] },
    package: { en: 'Package', da: 'Pakke', tags: ['product', 'produkt', 'delivery'] },
    'file-text': { en: 'Document', da: 'Dokument', tags: ['file', 'fil', 'text'] },
    paperclip: { en: 'Attachment', da: 'Vedhæftning', tags: ['file', 'bilag', 'knowledge'] },
    'clipboard-list': { en: 'Case list', da: 'Sagsliste', tags: ['tasks', 'list', 'liste'] },
    'notebook-pen': { en: 'Notes', da: 'Noter', tags: ['write', 'skriv', 'journal'] },
    folder: { en: 'Folder', da: 'Mappe', tags: ['category', 'kategori', 'group'] },
    layers: { en: 'Type', da: 'Type', tags: ['category', 'kategori', 'stack'] },
    tag: { en: 'Tag', da: 'Mærke', tags: ['label', 'etiket', 'tags'] },
    building: { en: 'Company', da: 'Virksomhed', tags: ['office', 'kontor', 'account'] },
    'building-2': { en: 'Department', da: 'Afdeling', tags: ['org', 'organisation', 'division'] },
    store: { en: 'Shop', da: 'Butik', tags: ['store', 'retail', 'forhandler'] },
    landmark: { en: 'Account', da: 'Konto', tags: ['bank', 'institution', 'customer'] },
    globe: { en: 'Web', da: 'Web', tags: ['internet', 'world', 'portal'] },
    house: { en: 'Home', da: 'Hjem', tags: ['property', 'bolig', 'ejendom'] },
    server: { en: 'Server', da: 'Server', tags: ['backend', 'infra', 'hosting'] },
    database: { en: 'Data', da: 'Data', tags: ['database', 'db', 'storage'] },
    cloud: { en: 'Cloud', da: 'Sky', tags: ['online', 'saas', 'azure'] },
    monitor: { en: 'Desktop', da: 'Skærm', tags: ['screen', 'computer', 'pc'] },
    smartphone: { en: 'Mobile', da: 'Mobil', tags: ['phone', 'app', 'telefon'] },
    'map-pin': { en: 'Location', da: 'Placering', tags: ['place', 'sted', 'address', 'adresse'] },
    funnel: { en: 'Filter', da: 'Filter', tags: ['default', 'standard', 'filter'] },
    telescope: { en: 'Explore', da: 'Udforsk', tags: ['discover', 'find', 'search'] },
    lightbulb: { en: 'Idea', da: 'Idé', tags: ['insight', 'indsigt', 'how-to'] },
    heart: { en: 'Sentiment', da: 'Stemning', tags: ['love', 'happy', 'glad'] },
    circle: { en: 'Circle', da: 'Cirkel', tags: ['shape', 'form', 'dot'] },
    square: { en: 'Square', da: 'Firkant', tags: ['shape', 'form', 'box'] },
    triangle: { en: 'Triangle', da: 'Trekant', tags: ['shape', 'form'] },
    hexagon: { en: 'Hexagon', da: 'Sekskant', tags: ['shape', 'form'] },
    diamond: { en: 'Diamond', da: 'Diamant', tags: ['shape', 'form', 'gem'] },
    hash: { en: 'Number', da: 'Nummer', tags: ['id', 'hash', 'count'] },
    eye: { en: 'Watch', da: 'Overvåg', tags: ['unreviewed', 'ikke gennemset', 'see', 'se'] },
    target: { en: 'Goal', da: 'Mål', tags: ['focus', 'fokus', 'target'] },
};
/** Picker search over English + Danish labels and synonyms. */
export function searchFilterIcons(query) {
    const q = query.trim().toLowerCase();
    if (!q)
        return [...FILTER_ICON_NAMES];
    return FILTER_ICON_NAMES.filter((n) => {
        const info = FILTER_ICON_INFO[n];
        return n.includes(q) || info.en.toLowerCase().includes(q) || info.da.toLowerCase().includes(q)
            || info.tags.some((t) => t.toLowerCase().includes(q));
    });
}
/**
 * The default icon for a filter, derived at SAVE time (stored with
 * icon_source 'derived'): the first condition's field icon (depth-first), with
 * the field's token overrides (Assignee is Me → user-check, Unassigned →
 * user-x), falling back to funnel.
 */
export function deriveFilterIcon(def, descriptor) {
    if (!def)
        return DEFAULT_FILTER_ICON;
    const first = listFilterConditions(def.root)[0];
    if (!first)
        return DEFAULT_FILTER_ICON;
    const field = descriptor.fields.find((f) => f.key === first.field);
    if (!field)
        return DEFAULT_FILTER_ICON;
    if (field.tokenIcons) {
        if (first.op === 'is_empty' && field.tokenIcons.empty)
            return field.tokenIcons.empty;
        if (first.values.length === 1) {
            if (isMeToken(first.values[0]) && field.tokenIcons.me)
                return field.tokenIcons.me;
            if (isEmptyToken(first.values[0]) && field.tokenIcons.empty)
                return field.tokenIcons.empty;
        }
    }
    return field.icon;
}
//# sourceMappingURL=icons.js.map